import { persistIdentity, type ResolvedIdentity, resolveIdentity } from '../utils/identity';

/**
 * Consent gate for the loader.
 *
 * The dealer's CMP owns the banner and the region decision; this controller is
 * the headless state it drives via `window.Ekho.consent`. It gates the loader's
 * non-essential side effects (identity cookies, attribution persistence,
 * analytics init, page-view firing) behind opt-in. Functional modules (chat,
 * express) load either way.
 *
 * States:
 *   - `not-required`: `requireConsent` unset or `false`. Tracking runs on load.
 *   - `pending`: consent required, not yet granted. Nothing is stored or fired;
 *     page-views buffer.
 *   - `granted`: the CMP called `grant()`. Side effects run, tracking is live.
 *     A re-grant after a revoke runs them again.
 *   - `revoked`: the CMP called `revoke()`. Identity and attribution are
 *     cleared, analytics stop.
 *
 * `grant()` and `revoke()` are idempotent toggles: each fires only on a real
 * transition, and the callbacks are persistent, so consent can be withdrawn and
 * re-granted any number of times.
 */
export const CONSENT_STATE = {
  NOT_REQUIRED: 'not-required',
  PENDING: 'pending',
  GRANTED: 'granted',
  REVOKED: 'revoked',
} as const;

export type ConsentState = (typeof CONSENT_STATE)[keyof typeof CONSENT_STATE];

interface ConsentController {
  state: ConsentState;
  /** Side effects run each time tracking becomes allowed (persistent, re-runnable). */
  onAllow: Array<() => void>;
  /** Side effects run each time consent is withdrawn (persistent, re-runnable). */
  onRevoke: Array<() => void>;
}

let controller: ConsentController | null = null;

/**
 * Set up the gate. `true` requires consent before tracking (`requireConsent`);
 * `false` bypasses it. Returns the initial state.
 */
export function initConsent(consentRequired: boolean): ConsentState {
  const state = consentRequired ? CONSENT_STATE.PENDING : CONSENT_STATE.NOT_REQUIRED;
  controller = { state, onAllow: [], onRevoke: [] };
  return state;
}

/** Whether non-essential storage and tracking may run right now. */
export function isTrackingAllowed(): boolean {
  return (
    controller?.state === CONSENT_STATE.NOT_REQUIRED || controller?.state === CONSENT_STATE.GRANTED
  );
}

function runAll(callbacks: Array<() => void>): void {
  for (const cb of callbacks) {
    try {
      cb();
    } catch {
      // A side effect must not throw into the caller, or the flush aborts and
      // leaves the loader half-initialized.
    }
  }
}

/**
 * Register a side effect to run whenever tracking becomes allowed. It stays
 * registered, so a revoke then re-grant runs it again, and it runs now if
 * tracking is already allowed (bypass). With no controller (pre-init) it runs
 * once and is not retained.
 */
export function whenTrackingAllowed(cb: () => void): void {
  if (!controller) {
    cb();
    return;
  }
  controller.onAllow.push(cb);
  if (isTrackingAllowed()) cb();
}

/** Register a side effect to run when consent is withdrawn. */
export function onConsentRevoked(cb: () => void): void {
  controller?.onRevoke.push(cb);
}

/**
 * Grant consent: move to `granted` and run the allow side effects. A no-op when
 * tracking is already allowed, so a duplicate grant does nothing. Runs from both
 * `pending` (first grant) and `revoked` (re-grant); the callbacks are persistent.
 */
export function grantConsent(): void {
  if (!controller || isTrackingAllowed()) return;
  controller.state = CONSENT_STATE.GRANTED;
  runAll(controller.onAllow);
}

/**
 * Withdraw consent: move to `revoked` and run the revoke side effects (clear
 * identity and attribution, stop analytics). Idempotent: a second revoke with no
 * grant in between does nothing, so teardown and `consent:revoked` fire once per
 * withdrawal.
 */
export function revokeConsent(): void {
  if (!controller || controller.state === CONSENT_STATE.REVOKED) return;
  controller.state = CONSENT_STATE.REVOKED;
  runAll(controller.onRevoke);
}

/** Current gate state. Reports `not-required` before init. */
export function consentStatus(): ConsentState {
  return controller?.state ?? CONSENT_STATE.NOT_REQUIRED;
}

/**
 * Identity for the checkout handoff: persist to cookies when tracking is
 * allowed, resolve in-memory otherwise. Both return the same value for a page
 * load, so the forwarded ID is stable in any consent state.
 */
export function getConsentedIdentity(): ResolvedIdentity {
  const ids = resolveIdentity();
  if (isTrackingAllowed()) persistIdentity();
  return ids;
}

/** Reset the gate. Exported for testing only. */
export function resetConsentForTesting(): void {
  controller = null;
}
